import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { State, Store } from '@ngrx/store';
import { IGeoCoords, Location, LocationService, MapsService, RideRequestLocation, Sublocation } from '@uzurv/clara';
import { ApplicationException, AutoCompleteResult, isNullOrUndefined } from '@uzurv/core';
import { Observable, combineLatest, map, of, switchMap, take } from 'rxjs';
import { ConfirmationDialogComponent } from '../../dialogs/confirmation-dialog/confirmation-dialog.component';
import * as fromLocationManager from '../store';
import { LocationManagerState } from '../store';

// TODO move to Pages and factor out individual components.

@Component({
  selector: 'app-location-manager-page',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './location-manager-page.component.html',
  styleUrls: ['./location-manager-page.component.scss']
})
export class LocationManagerPageComponent implements OnInit, OnDestroy {
  @Input() title: string;
  @Input() preloadedLocation: Location;
  @Input() managing = true;
  @Output() cancel = new EventEmitter<null>();
  @Output() selectLocation = new EventEmitter<RideRequestLocation>();
  searchString$: Observable<string> = this.store.select(fromLocationManager.getSearchString);
  loadingSearchResults$: Observable<boolean> = this.store.select(fromLocationManager.getLoadingSearchResults);
  autoCompleteResults$: Observable<Array<AutoCompleteResult>> = this.store.select(fromLocationManager.getAutocompleteResults);
  loadedLocation$: Observable<Location> = this.store.select(fromLocationManager.getLoadedLocation);
  mapLoaded$: Observable<boolean> = this.store.select(fromLocationManager.getMapLoaded);
  offsetLocationMode$: Observable<boolean> = this.store.select(fromLocationManager.getOffsetLocationMode);
  mapCenter$: Observable<IGeoCoords> = this.store.select(fromLocationManager.getMapCenterCoords);
  userLocation$: Observable<IGeoCoords> = this.store.select(fromLocationManager.getUserLocation);
  addingNewLocation$: Observable<boolean> = this.store.select(fromLocationManager.getAddingNewLocation);
  newLocationName$: Observable<string> = this.store.select(fromLocationManager.getNewLocationName);
  hoveredLocation$: Observable<Location | Sublocation> = this.store.select(fromLocationManager.getHoveredLocation);
  selectedLocation$: Observable<Location> = this.store.select(fromLocationManager.getSelectedLocation);
  geocodedOffsetLocation$: Observable<Location> = this.store.select(fromLocationManager.getGeocodedOffsetLocation);
  loading = false;

  constructor(
    private store: Store<State<LocationManagerState>>,
    private dialog: MatDialog,
    private mapsService: MapsService,
    private locationService: LocationService
  ) {}

  ngOnInit() {
    if (!isNullOrUndefined(this.preloadedLocation)) {
      this.store.dispatch(new fromLocationManager.SearchResultSelected(this.preloadedLocation.display));
    }
  }

  ngOnDestroy(): void {
    this.store.dispatch(new fromLocationManager.Reset());
  }

  handleSearch(searchString: string): void {
    this.store.dispatch(new fromLocationManager.Search(searchString));
  }

  handleClearSearch(): void {
    this.store.dispatch(new fromLocationManager.ClearSearch());
  }

  handleAutocompleteResultSelected(result: AutoCompleteResult): void {
    this.store.dispatch(new fromLocationManager.SearchResultSelected(result.label));
  }

  handleMapLoaded(): void {
    this.store.dispatch(new fromLocationManager.MapLoaded());
  }

  handleOffsetLocationMode(mode: boolean): void {
    this.store.dispatch(new fromLocationManager.UseOffsetLocation(mode));
  }

  handleMapMoved(coords: IGeoCoords): void {
    this.store.dispatch(new fromLocationManager.MoveMap(coords));
  }

  handleSelectLocation(location: Location) {
    this.store.dispatch(new fromLocationManager.LocationSelected(location));
  }

  handleAddNewLocation(): void {
    this.store.dispatch(new fromLocationManager.AddNewLocation());
  }

  handleCancelAddNewLocation(): void {
    this.store.dispatch(new fromLocationManager.CancelAddNewLocation());
  }

  handleSaveNewLocation(description: string): void {
    if (description.length < 1) {
      this.store.dispatch(new fromLocationManager.NewLocationInvalid(new ApplicationException('The new location name cannot be empty')));
    } else {
      this.store.dispatch(new fromLocationManager.SetNewLocationName(description));
      this.store.dispatch(new fromLocationManager.SubmitNewLocation());
    }
  }

  handleLocationMouseover(location: Location | Sublocation) {
    this.store.dispatch(new fromLocationManager.LocationMouseover(location));
  }

  handleLocationMouseout(location: Location | Sublocation) {
    this.store.dispatch(new fromLocationManager.LocationMouseout(location));
  }

  handleDeleteSublocation(sublocation: Sublocation) {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Delete sublocation?',
          message: 'This action is not reversible'
        }
      })
      .afterClosed()
      .subscribe(confirmed => {
        if (confirmed) {
          this.store.dispatch(new fromLocationManager.DeleteSublocation(sublocation));
        }
      });
  }

  handleUseLocation(): void {
    this.loading = true;
    combineLatest([this.selectedLocation$, this.offsetLocationMode$, this.mapCenter$])
      .pipe(
        take(1),
        switchMap(([selectedLocation, offsetLocationMode, mapCenter]) => {
          if (!isNullOrUndefined(selectedLocation)) {
            this.loading = false;
            return of(this.selectLocation.emit(this.locationService.locationToRideRequestLocation(selectedLocation)));
          } else if (offsetLocationMode) {
            return this.mapsService.geocodeLocationByLatLng(mapCenter.lat, mapCenter.lng).pipe(
              switchMap(geocodedLocation =>
                this.locationService.addLocation(geocodedLocation.display).pipe(
                  map(savedLocation => {
                    this.selectLocation.emit(this.locationService.locationToRideRequestLocation(savedLocation));
                    this.loading = false;
                  })
                )
              )
            );
          }
        })
      )
      .subscribe();
  }
}
